import { useNavigate } from 'react-router-dom';
import { PhoneCall, CheckCircle2 } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';

/**
 * Casing → Intake. A PLACEHOLDER, deliberately, and it stays one.
 *
 * AI phone answering ships in Trucking and does not ship here. The page exists
 * so the sidebar entry leads somewhere that says so, rather than to a 404 or to
 * an empty screen a reader would file as a bug.
 *
 * NO STATS ON THE BAND, AND THAT IS THE WHOLE POINT OF NOT HAVING ANY.
 * `PageHeaderBand`'s `stats` prop is optional and the component renders no
 * strip when it is absent — which is what a page with nothing to count is owed.
 * The alternative on offer was a strip of zeros, and a `0` under "Calls
 * answered" is a claim that the feature ran and answered none. It has not run.
 * See the em-dash rule in CLAUDE.md: absent is not zero, and here the figure is
 * not even absent — there is no figure, because there is no subject.
 *
 * It is also the first stat-less band in the app, so it is worth saying what
 * that does to the clearance measurement rather than leaving the next reader to
 * wonder. The circle's `right:` offset was swept against STAT TEXT — dim
 * labels and sub-lines in columns three and four, at the top right of the band.
 * With no strip there is no ink in those columns at all; the only ink on this
 * band is the eyebrow and the title, both in the left copy column, which no
 * sweep has ever found the circle near. Measured anyway, alongside Quotes in
 * the same run, and reported in the PR.
 */
const CasingIntakeScreen = () => {
  const navigate = useNavigate();

  return (
    <div data-testid="casing-intake-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Casing operations"
        title="Intake"
        onBack={() => navigate(-1)}
        backLabel="Back"
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* Full width. The card sat in a max-w-2xl box on a full-bleed page,
              so it read as a half-width panel floating in empty grey; the body
              idiom is one panel across the content column. */}
          <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-fieldiq-accent/10 flex items-center justify-center">
                <PhoneCall className="w-6 h-6 text-fieldiq-accent" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-fieldiq-text">AI Phone Answering</h2>
                <p className="text-sm text-fieldiq-muted">Coming soon to casing</p>
              </div>
            </div>

            <p className="text-fieldiq-text mb-6">
              Let an AI agent answer inbound casing job calls and prepare a job draft before your dispatcher even hangs up.
            </p>

            <div className="space-y-3 mb-6">
              <h3 className="text-sm font-semibold text-fieldiq-text uppercase tracking-wide">What it will do</h3>
              {[
                'Answer inbound calls with a natural-sounding AI agent',
                'Extract well location, casing size, grade, weight, and connection type',
                'Capture crew requirements, spud date, and special instructions',
                'Create a pending job draft routed to your dispatcher for review',
                'Maintain a full audit trail of every AI decision and approval',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-fieldiq-accent mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-fieldiq-muted">{item}</p>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-fieldiq-border">
              <p className="text-xs text-fieldiq-muted">
                On the roadmap — no ETA yet. Available today in our Trucking vertical.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CasingIntakeScreen;
